import { Component, OnDestroy, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Events } from '../../helpers/events';

// ionic
import { ModalController } from '@ionic/angular';

// other libraries
import { LangChangeEvent, TranslateService } from "@ngx-translate/core";

// rxjs
import { Subject } from "rxjs";
import { take, takeUntil } from "rxjs/operators";

// components
import { LiveVotingComponent } from "../live-voting/live-voting.component";
import { InvitationsComponent } from "../invitations/invitations.component";

// providers
import { EventService } from "../../services/event.service";
import { FeedService } from "../../services/feed.service";
import { UserService } from "../../services/user.service";
import { OverlayService } from "../../services/overlay.service";
import { PlatformService } from "../../services/platform.service";
import { VotingService } from "../../services/voting.service";
import { RouterExtService } from 'src/app/services/router-ext.service';

// models
import { Event } from "../../models/event";
import { Feed } from "../../models/feed";
import { User } from "../../models/user";
import { ChatGroup } from "../../models/chat-group";
import { Voting } from "../../models/voting";
import { AuthenticationService } from 'src/app/services/authentication.service';

@Component({
    selector: 'header-notifications',
    templateUrl: './header-notifications.component.html',
    styleUrls: ['./header-notifications.component.scss'],
})
export class HeaderNotificationsComponent implements OnDestroy {


    /**
     * apply styles for notification in messages pages
     *
     * @type {boolean>}
     */
    @Input() chatView: boolean = false;

    /**
     * apply styles for notification in messages pages
     *
     * @type {boolean>}
     */
    @Input() callView: boolean = false;

    /**
     * apply styles for notification in stream fullscreen page
     *
     * @type {boolean>}
     */
    @Input() streamView: boolean = false;

    /**
     * apply styles for notification in stream fullscreen page
     *
     * @type {boolean>}
     */
    @Input() detailVisible: boolean = false;

    /**
     * unsubscribe subject
     *
     * @type {Subject<void>}
     */
    private ngUnsubscribe: Subject<void> = new Subject<void>();

    /**
     * current event
     *
     * @type {User}
     */
    public user: User;

    /**
     * selected language
     *
     * @type string
     */
    public userLang: string;

    /**
     * event
     *
     * @type {Event}
     */
    public event: Event;

    /**
     * count of actual invitations to event
     *
     * @type {Number}
     */
    public invitationCount = 0;

    /**
     * current invitation details
     *
     * @type {}
     */
    public invitation;

    public liveVotings;

    private activeTousts = [];

    // if we want to display only live voting in chat group
    @Input() chatGroupLiveVoting: boolean = false;

    /**
     * selected channel chat
     *
     * @type {number}
     */
    @Input() selectedChatGroup: ChatGroup;

    // handlers for events
    chatGroupSelectedHandler;

    private directOpenLiveVotings: Feed[] = [];

    /**
     * constructor
     *
     * @param pushPageService
     * @param userProvider
     * @param appEvents
     */
    constructor(
        private router: Router,
        private routerExtService: RouterExtService,
        private auth: AuthenticationService,
        public eventService: EventService,
        public feedService: FeedService,
        public userService: UserService,
        public overlayService: OverlayService,
        public translate: TranslateService,
        public plt: PlatformService,
        public appEvents: Events,
        public modalController: ModalController,
        public votingService: VotingService
    ) {
        this.userLang = this.translate.currentLang;

        this.eventService.getCurrentEvent().pipe(
            takeUntil(this.ngUnsubscribe)
        ).subscribe((event) => {
            this.event = event;
        });

        this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
            this.userLang = event.lang;
            // update toasts with correct translations
            // NOTE[jg] replace activeToast with overlayService.notifications??
            this.activeTousts.forEach((item) => {
                let osNotification = this.overlayService.notifications['call' + item.participant_id];
                if (osNotification) {
                    this.translate.get(['VIDEOCALL_INFO', 'BUTTON_DECLINE', 'BUTTON_ACCEPT'], {
                        name: item.participant_name
                    }).subscribe((text) => {
                        osNotification.buttons[0].text = text.BUTTON_DECLINE;
                        osNotification.buttons[1].text = text.BUTTON_ACCEPT;
                        osNotification.message = text.VIDEOCALL_INFO;
                    });
                }
            });
        });
    }

    ngOnInit() {
        if (this.chatGroupLiveVoting) {
            this.chatGroupSelectedHandler = (selectedChatGroup?: ChatGroup) => {
                // refresh notifications only in case of change of chat group
                if (selectedChatGroup.id != this.selectedChatGroup.id) {
                    this.selectedChatGroup = selectedChatGroup;
                    this.refreshChatGroupLiveVoting();
                } else {
                    this.selectedChatGroup = selectedChatGroup;
                }
            }

            this.appEvents.subscribe('messages:chatgroup', this.chatGroupSelectedHandler);
        }

        this.userService.getCurrentUser().pipe(
            takeUntil(this.ngUnsubscribe)
        ).subscribe(
            (user: User) => {

                this.user = user;

                this.invitation = null;
                this.invitationCount = 0;

                let lines = 0;

                // check notifications
                if (this.user.hasOwnProperty('header_notifications')) {
                    this.user.header_notifications.forEach((notification) => {

                        if (!this.chatGroupLiveVoting) {
                            // if there is any invitations, enable top banner
                            if (notification.data.type == 'invitation') {
                                this.invitationCount++;
                                this.invitation = notification.data;
                                lines = 1;
                            }

                            if (notification.data.type == 'invitation-to-community') {
                                // show universal message
                                this.invitationCount = this.invitationCount + 2;
                                lines = 1;
                            }

                            // show toast with info about new video call, if this was not already showned
                            if (notification.data.type == 'call') {
                                if (this.overlayService.waitingCalls.indexOf(notification.data.chime_meeting_id) == -1) {
                                    this.overlayService.waitingCalls.push(notification.data.chime_meeting_id);
                                    this.showCall(notification);
                                } else {
                                    // mark this notification as read, because there is another one or call is already running/finished
                                    this.markNotificationAsRead(notification);
                                }
                            }

                            // show info about soon call expiration
                            if (notification.data.type == 'expiring_call') {
                                if (
                                    notification.data.chime_meeting_id == this.overlayService.liveCallId
                                    && !this.overlayService.notifications[notification.data.type + notification.data.chime_meeting_id]
                                ) {
                                    this.overlayService.presentToast(
                                        // this.translate.instant('VIDEOCALL_NEW'),
                                        this.translate.instant('VIDEOCALL_EXPIRING_INFO'),
                                        'warning',
                                        false,
                                        0,
                                        [{
                                            text: this.translate.instant('BUTTON_CANCEL'),
                                            cssClass: 'secondary',
                                            handler: () => {
                                                // remove notification
                                                this.overlayService.notifications[notification.data.type + notification.data.chime_meeting_id] = null;
                                            }
                                        }],
                                        'with-button',
                                        notification.data.type + notification.data.chime_meeting_id
                                    );
                                }
                                // allways mark notification as read to hide it for next calls,
                                //  which can be started after receiving this notification
                                this.markNotificationAsRead(notification);
                            }
                        }
                    });

                    // process chat group notifications
                    if (this.chatGroupLiveVoting) {
                        this.refreshChatGroupLiveVoting();
                    }
                    else {
                        // use normal live voting notificaitons
                        this.liveVotings = this.user.header_notifications.filter((notification) => {
                            return notification.data.type == 'live-voting'
                        }).slice(0, (this.plt.tabMenu ? 1 : 999));

                        if (this.liveVotings.length > 0) {
                            lines += 1;
                        }
                    }

                    // update css property with header additional height
                    const doc = document.documentElement;
                    if (this.plt.sizeXl) {
                        doc.style.setProperty('--header-height', `${lines * 72}px`)
                    } else {
                        doc.style.setProperty('--header-height', `${lines * 56}px`)
                    }

                    setTimeout(() => {
                        this.appEvents.publish('header:changed', lines);
                    }, 200);

                }
            });
    }

    private refreshChatGroupLiveVoting() {
        // process chat group notifications
        if (this.user && this.chatGroupLiveVoting && this.selectedChatGroup) {
            // return list of available valid voting IDs
            this.votingService.getLiveVotingIds(this.user.selected_participant_id, this.selectedChatGroup.id).subscribe(
                (votingIds: number[]) => {
                    if (votingIds.length) {
                        // let lines = 0;

                        // use live voting notifications for chat group
                        this.liveVotings = this.user.header_notifications.filter((notification) => {
                            return (notification.data.type == 'live-voting-chat-group'
                                || notification.data.type == 'live-voting-chat-group-direct-open')
                                && notification.data.chat_group_ids?.includes(this.selectedChatGroup.id)
                                // check also if ID is still valid
                                && votingIds.includes(notification.data.voting_id)
                        });

                        // NOTE[jg] - for case we want to show allways live votings, just copy all available votings
                        this.directOpenLiveVotings = [...this.liveVotings];
                        // NOTE[jg] - version for opening only first time of live voting is available
                        // - problem is about returning back status of direct opening status
                        // direct open live voting if there is some and put other waiting in qeue
                        // this.directOpenLiveVotings = this.user.header_notifications.filter((notification) => {
                        //     return (notification.data.type == 'live-voting-chat-group-direct-open')
                        //         && notification.data.chat_group_ids ?.includes(this.selectedChatGroup.id)
                        //         // check also if ID is still valid
                        //         && votingIds.includes(notification.data.voting_id)
                        //     });

                        // show first live voting if there is any and remove it from next opening queue
                        // show those live votings only first time, they are available
                        if (this.directOpenLiveVotings.length > 0) {
                            this.showLiveVoting(this.directOpenLiveVotings.shift());
                        }
                    } else {
                        // there is no live voting for this chat group
                        this.directOpenLiveVotings = [];
                        this.liveVotings = [];
                    }
                });
        }
    }

    /**
     * accept notification feed
     * @param Feed feed
     * @return void
     */
    public feedAccept(feed: Feed) {
        this.feedService.accept(feed).subscribe(
            success => {
                this.feedRequestSuccess(feed, success);
                // TODO
                //this.appEvents.broadcast('user:current:participant');
            },
            error => {
                this.feedRequestError(error);
            }
        );
    }

    /**
     * display notification Action
     * @param Feed feed
     * @return void
     */
    public feedDecline(feed: Feed) {
        this.feedService.decline(feed).subscribe(
            success => {
                this.feedRequestSuccess(feed, success);
                // TODO: display voting page
            },
            error => {
                this.feedRequestError(error);
            }
        );
    }

    /**
     * postpone notification feed
     * @param Feed feed
     * @return void
     */
    public feedPostpone(feed: Feed) {
        this.feedService.postpone(feed).subscribe(
            success => {
                let remove = true;

                let feedObj = this.user.header_notifications.find((item) => {
                    return item.id == feed.id;
                });

                if (feedObj?.data?.type == 'live-voting-chat-group-direct-open') {
                    remove = false;
                }

                this.feedRequestSuccess(feed, success, remove);
            },
            error => {
                this.feedRequestError(error);
            }
        );
    }

    /**
     * handle feed request success
     * @param Feed feed
     * @return void
     */
    public feedRequestSuccess(feed: Feed, success, remove: boolean = true) {
        {
            if (success.message) {
                this.overlayService.showSuccess(success.message);
            }

            if (remove) {
                this.user.header_notifications = this.user.header_notifications.filter(item => {
                    return item.id !== feed.id;
                });
            }

            // update token in case that it is refreshed
            if (success.token) {
                this.overlayService.showLoading();
                this.auth.updateToken(success.token, true);
            } else {

                // redirect to accepted event
                if (!this.chatGroupLiveVoting) {
                    // this.overlayService.showLoading();
                    // this.appEvents.publish('user:current:participant', {
                    //     redirectLink: this.plt.defaultLink + '/home/info/matching'
                    // });
                }
            }
        }
    }

    /**
     * handle feed request error
     * @return void
     */
    public feedRequestError(error) {
        {
            this.overlayService.showError(error.error.message);
        }
    }

    /**
     * redirect user to live voting section
     * @return void
     */
    public checkNotificationEvent(notification) {
        // if voting is from different event, switch to that event and redirect...
        if (this.event && notification.event_id != this.event.id) {
            this.overlayService.showLoading();
            this.userService.setSelectedEvent(this.user.id, notification.event_id)
                .subscribe(
                    (user: User) => {

                        // reset subscription
                        this.appEvents.unsubscribe('refresh:messages');
                        this.appEvents.unsubscribe('refresh:messages:live');
                        this.appEvents.unsubscribe('refresh:appointments');
                        this.appEvents.unsubscribe('attendee:changed');
                        this.appEvents.unsubscribe('attendee:filter');
                        this.appEvents.unsubscribe('messages:refresh:list');
                        this.appEvents.unsubscribe('messages:participant');
                        this.appEvents.unsubscribe('messages:broadcast');
                        this.appEvents.unsubscribe('messages:chatgroup');
                        this.appEvents.unsubscribe('messages:chatgroup:add');
                        this.appEvents.unsubscribe('messages:empty');
                        // reset notifications and wait for loading for this event
                        this.plt.resetNotifications();
                        // update user and event
                        this.userService.setCurrentUser(new User(user));
                        // wait till latest current user is loaded
                        this.userService.getCurrentUser().pipe(take(1)).subscribe((user) => {
                            // setTimeout(() => {
                            this.overlayService.hideLoading();
                            if (notification.data.type == 'live-voting') {
                                // redirect to home
                                this.router.navigate([this.plt.defaultLink + '/home/info/matching']);
                                // show modal with livevoting
                                this.showLiveVoting(notification);
                            }
                            if (notification.data.type == 'call') {
                                // redirect to call page if on other page
                                if (window.location.pathname != this.plt.defaultLink + '/messages/direct/' + notification.data.participant_id) {
                                    this.router.navigate([this.plt.defaultLink + '/messages/direct/' + notification.data.participant_id], {
                                        replaceUrl: true,
                                        queryParams: {
                                            call: true
                                        }
                                    });
                                } else {
                                    this.appEvents.publish('messages:call', true);
                                }
                            }

                            // / update token in case that it is refreshed
                            if (user.token) {
                                this.auth.updateToken(user.token);
                            }
                            // }, 500);
                        });
                    }, error => {
                        this.overlayService.showError(error.error.message);
                    });
        } else {
            if (notification.data.type == 'live-voting' || notification.data.type == 'live-voting-chat-group' || notification.data.type == 'live-voting-chat-group-direct-open') {
                // copy other to queue for showing after closing first
                this.refreshChatGroupLiveVoting();
                this.showLiveVoting(notification);
            }
            if (notification.data.type == 'call') {
                // redirect to call page
                if (window.location.pathname != this.plt.defaultLink + '/messages/direct/' + notification.data.participant_id) {
                    this.router.navigate([this.plt.defaultLink + '/messages/direct/' + notification.data.participant_id], {
                        replaceUrl: true,
                        queryParams: {
                            call: true
                        }
                    });
                } else {
                    this.appEvents.publish('messages:call', true);
                }
            }
        }
    }

    // show modal with live voting info
    public async showLiveVoting(notification) {
        if (!this.plt.votingOpened) {
            this.plt.votingOpened = true;

            // update url
            let link = this.routerExtService.currentUrl.split("?")[0].split("#")[0] + '?voting_id=' + notification.data.voting_id.toString();
            // add event id also
            link += '&event_id=' + this.event.id.toString();
            this.routerExtService.softNavigate(link);
            // this.routerExtService.softNavigate();

            const modal = await this.modalController.create({
                component: LiveVotingComponent,
                cssClass: 'attendee-modal',
                componentProps: {
                    showPostpone: notification.data.type == 'live-voting-chat-group-direct-open',
                    votingId: notification.data.voting_id
                }
            });

            modal.onWillDismiss().then((data) => {
                this.plt.votingOpened = false;

                if (this.directOpenLiveVotings.length > 0) {
                    this.showLiveVoting(this.directOpenLiveVotings.pop());
                }

                if (data.data?.action == 'postpone') {
                    this.feedPostpone(notification);
                }

                if (data.data?.action == 'close' || data.data?.action == 'postpone' || data.role == 'backdrop') {

                    let link = this.plt.defaultLink + this.routerExtService.currentUrl.split("?")[0].split("#")[0].replace('/tabs', '');
                    if (this.plt.tabMenu && !this.routerExtService.currentUrl.includes('/tabs')) {
                        this.router.navigateByUrl(link);
                    } else {
                        this.routerExtService.softNavigate(link);
                    }

                } else {
                    // do nothing for back navigation from browser..
                    // just close modal and update history
                    this.routerExtService.popHistory();
                }
            });

            return await modal.present();
        }

        return await false;
    }


    // show modal with call info
    public showCall(notification) {

        this.translate.get('VIDEOCALL_INFO', {
            name: notification.data.participant_name
        }).subscribe((text) => {
            // show toast notification with buttons
            this.overlayService.presentToast(
                // this.translate.instant('VIDEOCALL_NEW'),
                text,
                'primary',
                false,
                0,
                [
                    {
                        text: this.translate.instant('BUTTON_DECLINE'),
                        cssClass: 'secondary',
                        handler: () => {
                            // remove call
                            this.overlayService.endCall(notification.data.chime_meeting_id);
                            // close on cancel
                            this.markNotificationAsRead(notification);
                            this.activeTousts = this.activeTousts.filter((item) => { return item.participant_id != notification.data.participant_id });
                        }
                    },
                    {
                        text: this.translate.instant('BUTTON_ACCEPT'),
                        handler: () => {
                            this.checkNotificationEvent(notification);
                            this.activeTousts = this.activeTousts.filter((item) => { return item.participant_id != notification.data.participant_id });
                        }
                    }
                ],
                'with-buttons',
                notification.data.type + notification.data.participant_id
            );
            let activeToast = {
                participant_id: notification.data.participant_id,
                participant_name: notification.data.participant_name
            };
            this.activeTousts.push(activeToast);
        });
    }

    public async openInvitations() {

        this.routerExtService.softNavigate();

        const modal = await this.modalController.create({
            component: InvitationsComponent,
            cssClass: 'invitations-modal',
            componentProps: {

            }
        });

        modal.onWillDismiss().then((data) => {
            if (data.data?.action == 'close' || data.role == 'backdrop') {
                this.routerExtService.softBack();
            }
        });

        return await modal.present();
    }

    markNotificationAsRead(notification) {
        this.feedService.decline(notification).subscribe(
            success => {
                this.user.header_notifications = this.user.header_notifications.filter(item => {
                    return item.id !== notification.id;
                });
            },
            error => {
                this.feedRequestError(error);
            }
        );
    }

    /**
     * on destroy
     *
     * @return void
     */
    ngOnDestroy() {
        if (this.chatGroupSelectedHandler) {
            this.appEvents.unsubscribe('messages:chatgroup', this.chatGroupSelectedHandler);
        }
        this.overlayService.closeModals();
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

}
